/**
 * BrandsAdmin (PROPOSAL-EQUIPMENT-02, Seth 2026-10-29: "just use brand logos for ones we
 * don't have product pictures for in the pricebook") — Settings → Brands & Logos.
 *
 * One row per manufacturer: the name a homeowner reads, the ESC spellings it stands for
 * (aliases), and the logo file. The logo is the manufacturer's own dealer asset the office
 * uploads (Lennox, Carrier/Bryant, A. O. Smith … all publish dealer logo kits) — Pegasus
 * never draws one. Where the pricebook has no product photo, the homeowner's equipment
 * tile shows this logo; the 360 can read the same table next. The spellings list at the
 * bottom shows what the equipment record says and how many units say it, so a spelling
 * no brand claims yet ("TRAIN", "FUJISTU") becomes an alias in one edit.
 */
import { useMemo, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Archive, Check, Pencil, Plus, RotateCcw, Tag, Upload, X } from "lucide-react";
import { Card } from "@/components/ui/Card";
import { InfoTip } from "@/components/InfoTip";
import { cn } from "@/lib/cn";
import { supabase } from "@/lib/supabase";
import { toastError } from "@/components/ui/mutationToast";

export interface BrandRow {
  slug: string;
  name: string;
  aliases: string[];
  logo_url: string | null;
  sort: number;
  is_active: boolean;
}

const BUCKET = "brand-logos";

function useBrands() {
  return useQuery({
    queryKey: ["pegasus_brand"],
    queryFn: async (): Promise<BrandRow[]> => {
      const { data, error } = await supabase.from("pegasus_brand").select("slug, name, aliases, logo_url, sort, is_active").order("sort").order("name");
      if (error) throw error;
      return (data ?? []) as BrandRow[];
    },
    staleTime: 60_000,
  });
}

/** The spellings on the equipment record ("LENNOX" 13,443 · "TRAIN" 5), for the counts and the unclaimed list. */
function useMfgSpellings() {
  return useQuery({
    queryKey: ["pegasus_mfg_spellings"],
    queryFn: async (): Promise<Array<{ spelling: string; n: number }>> => {
      const { data, error } = await supabase.rpc("pegasus_mfg_spellings", { p_min: 3 });
      if (error) throw error;
      return ((data ?? []) as Array<{ spelling: string; n: number | string }>).map((r) => ({ spelling: r.spelling, n: Number(r.n) }));
    },
    staleTime: 5 * 60_000,
  });
}

/** Does this spelling belong to the brand — the same rule as pegasus_brand_for_mfg. */
const claims = (b: BrandRow, spelling: string): boolean => {
  const s = spelling.toUpperCase();
  return s === b.name.toUpperCase() || b.aliases.includes(s) || s.startsWith(b.name.toUpperCase() + " ");
};

const slugify = (name: string) => name.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "").slice(0, 40) || `brand-${Date.now()}`;

export function BrandsAdmin() {
  const qc = useQueryClient();
  const { data, isLoading } = useBrands();
  const spellings = useMfgSpellings().data ?? [];
  const [adding, setAdding] = useState(false);
  const [newName, setNewName] = useState("");
  const [newAliases, setNewAliases] = useState("");
  const [editSlug, setEditSlug] = useState<string | null>(null);
  const [editName, setEditName] = useState("");
  const [editAliases, setEditAliases] = useState("");
  const [showArchived, setShowArchived] = useState(false);
  const [uploading, setUploading] = useState<string | null>(null);
  const fileRefs = useRef<Record<string, HTMLInputElement | null>>({});

  const rows = data ?? [];
  const active = rows.filter((r) => r.is_active);
  const archived = rows.filter((r) => !r.is_active);

  // Units per brand, and the spellings nobody claims (worth an alias when the count is real).
  const unitCount = useMemo(() => {
    const m = new Map<string, number>();
    for (const b of rows) m.set(b.slug, spellings.filter((s) => claims(b, s.spelling)).reduce((t, s) => t + s.n, 0));
    return m;
  }, [rows, spellings]);
  const unclaimed = useMemo(
    () => spellings.filter((s) => !rows.some((b) => b.is_active && claims(b, s.spelling))).slice(0, 40),
    [rows, spellings],
  );

  const save = useMutation({
    mutationFn: async (v: { slug: string; patch: Partial<Pick<BrandRow, "name" | "aliases" | "logo_url" | "is_active">> }) => {
      const { error } = await supabase.from("pegasus_brand").update({ ...v.patch, updated_at: new Date().toISOString() }).eq("slug", v.slug);
      if (error) throw error;
    },
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ["pegasus_brand"] }); },
    onError: (e) => toastError((e as Error).message),
  });
  const add = useMutation({
    mutationFn: async (v: { name: string; aliases: string[] }) => {
      const { error } = await supabase.from("pegasus_brand").insert({ slug: slugify(v.name), name: v.name, aliases: v.aliases, sort: 100 });
      if (error) throw error;
    },
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ["pegasus_brand"] }); setAdding(false); setNewName(""); setNewAliases(""); },
    onError: (e) => toastError((e as Error).message),
  });

  const parseAliases = (s: string) => [...new Set(s.split(/[,\n;]/).map((a) => a.trim().toUpperCase()).filter(Boolean))];

  const upload = async (b: BrandRow, file: File) => {
    setUploading(b.slug);
    try {
      const ext = (file.name.split(".").pop() || "png").toLowerCase().replace(/[^a-z0-9]/g, "") || "png";
      const path = `${b.slug}-${Date.now()}.${ext}`;
      const { error } = await supabase.storage.from(BUCKET).upload(path, file, { contentType: file.type || "image/png", upsert: false });
      if (error) throw error;
      const url = supabase.storage.from(BUCKET).getPublicUrl(path).data.publicUrl;
      await save.mutateAsync({ slug: b.slug, patch: { logo_url: url } });
    } catch (e) {
      toastError((e as Error).message || "Upload failed.");
    } finally {
      setUploading(null);
    }
  };

  const startEdit = (b: BrandRow) => { setEditSlug(b.slug); setEditName(b.name); setEditAliases(b.aliases.join(", ")); };

  return (
    <Card className="overflow-hidden">
      <div className="flex items-center justify-between border-b border-slate-100 bg-gradient-to-r from-sky-50/70 via-transparent to-transparent px-4 py-2.5">
        <div className="flex items-center gap-2">
          <span className="flex h-6 w-6 items-center justify-center rounded-lg bg-sky-50 text-sky-600">
            <Tag className="h-3.5 w-3.5" />
          </span>
          <span className="text-[13px] font-bold text-slate-800">Brands &amp; Logos</span>
          <InfoTip label="About brands and logos">
            The homeowner&apos;s equipment tile shows the pricebook&apos;s product photo when there is one; otherwise the brand&apos;s logo from here;
            otherwise the kind of unit. Upload each manufacturer&apos;s own dealer logo file (PNG or SVG, on a transparent or white background).
            Aliases are the spellings the equipment record uses for that brand — &ldquo;AO SMITH&rdquo;, &ldquo;A.O. SMITH&rdquo; — so every unit finds its logo.
          </InfoTip>
        </div>
        <button type="button" onClick={() => setAdding((v) => !v)} className="inline-flex items-center gap-1 text-xs font-medium text-brand-700 transition-colors hover:text-brand-900">
          <Plus className="h-3 w-3" /> Add brand
        </button>
      </div>

      <div className="px-4 py-3">
        {adding && (
          <form
            onSubmit={(e) => { e.preventDefault(); if (newName.trim()) add.mutate({ name: newName.trim(), aliases: parseAliases(newAliases) }); }}
            className="mb-3 flex flex-wrap items-center gap-2 rounded-lg border border-hairline bg-slate-50/60 p-2"
          >
            <input autoFocus value={newName} onChange={(e) => setNewName(e.target.value)} placeholder="Brand name, e.g. Bradford White"
              className="min-w-[180px] flex-1 rounded-md border border-hairline bg-white px-2 py-1.5 text-sm focus:border-brand-400 focus:outline-none focus:ring-2 focus:ring-brand-400/30" />
            <input value={newAliases} onChange={(e) => setNewAliases(e.target.value)} placeholder="Spellings on the record, comma-separated"
              className="min-w-[220px] flex-[2] rounded-md border border-hairline bg-white px-2 py-1.5 text-sm focus:border-brand-400 focus:outline-none focus:ring-2 focus:ring-brand-400/30" />
            <button type="submit" disabled={!newName.trim() || add.isPending} className="rounded-md bg-brand-700 px-3 py-1.5 text-xs font-semibold text-white transition-colors hover:bg-brand-800 disabled:bg-brand-300">
              {add.isPending ? "Saving…" : "Add"}
            </button>
          </form>
        )}

        {isLoading ? (
          <div className="space-y-1.5">{Array.from({ length: 6 }).map((_, i) => <div key={i} className="h-10 animate-pulse rounded-lg bg-slate-100" />)}</div>
        ) : (
          <ul className="divide-y divide-slate-100">
            {active.map((b) => {
              const n = unitCount.get(b.slug) ?? 0;
              const editing = editSlug === b.slug;
              return (
                <li key={b.slug} className="group flex items-center gap-3 py-2">
                  {/* the logo slot — the file shows exactly as the homeowner's tile shows it */}
                  <button type="button" onClick={() => fileRefs.current[b.slug]?.click()} disabled={uploading === b.slug}
                    title={b.logo_url ? "Replace the logo" : "Upload a logo"}
                    className={cn("grid h-11 w-20 shrink-0 place-items-center overflow-hidden rounded-lg border bg-white transition-colors", b.logo_url ? "border-hairline hover:border-brand-400" : "border-dashed border-slate-300 text-slate-400 hover:border-brand-400 hover:text-brand-700")}>
                    {uploading === b.slug ? <span className="text-[10px] font-semibold">Uploading…</span>
                      : b.logo_url ? <img src={b.logo_url} alt={b.name} className="max-h-9 max-w-[4.5rem] object-contain" />
                      : <span className="inline-flex items-center gap-1 text-[10.5px] font-semibold"><Upload className="h-3 w-3" /> Logo</span>}
                  </button>
                  <input ref={(el) => { fileRefs.current[b.slug] = el; }} type="file" accept="image/png,image/svg+xml,image/webp,image/jpeg" className="hidden"
                    onChange={(e) => { const f = e.target.files?.[0]; if (f) void upload(b, f); e.target.value = ""; }} />
                  {editing ? (
                    <form onSubmit={(e) => { e.preventDefault(); save.mutate({ slug: b.slug, patch: { name: editName.trim() || b.name, aliases: parseAliases(editAliases) } }, { onSuccess: () => setEditSlug(null) }); }}
                      className="flex min-w-0 flex-1 flex-wrap items-center gap-2">
                      <input autoFocus value={editName} onChange={(e) => setEditName(e.target.value)} className="min-w-[140px] rounded-md border border-hairline bg-white px-2 py-1 text-sm focus:border-brand-400 focus:outline-none" />
                      <input value={editAliases} onChange={(e) => setEditAliases(e.target.value)} placeholder="Spellings, comma-separated" className="min-w-[220px] flex-1 rounded-md border border-hairline bg-white px-2 py-1 text-sm focus:border-brand-400 focus:outline-none" />
                      <button type="submit" className="rounded-md bg-brand-700 p-1.5 text-white hover:bg-brand-800" aria-label="Save"><Check className="h-3.5 w-3.5" /></button>
                      <button type="button" onClick={() => setEditSlug(null)} className="rounded-md p-1.5 text-slate-400 hover:bg-slate-100 hover:text-slate-700" aria-label="Cancel"><X className="h-3.5 w-3.5" /></button>
                    </form>
                  ) : (
                    <>
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-semibold text-slate-800">{b.name}</span>
                          {n > 0 && <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[10.5px] font-semibold tabular-nums text-slate-600">{n.toLocaleString()} unit{n === 1 ? "" : "s"}</span>}
                          {!b.logo_url && <span className="text-[10.5px] font-semibold text-amber-700">no logo yet</span>}
                        </div>
                        <div className="truncate text-[11.5px] text-slate-400">{b.aliases.length ? b.aliases.join(" · ") : "no spellings listed"}</div>
                      </div>
                      <button type="button" onClick={() => startEdit(b)} className="rounded-md p-1.5 text-slate-300 opacity-0 transition-opacity hover:bg-slate-100 hover:text-slate-700 group-hover:opacity-100" aria-label={`Edit ${b.name}`}><Pencil className="h-3.5 w-3.5" /></button>
                      <button type="button" onClick={() => save.mutate({ slug: b.slug, patch: { is_active: false } })} className="rounded-md p-1.5 text-slate-300 opacity-0 transition-opacity hover:bg-slate-100 hover:text-slate-700 group-hover:opacity-100" aria-label={`Archive ${b.name}`}><Archive className="h-3.5 w-3.5" /></button>
                    </>
                  )}
                </li>
              );
            })}
          </ul>
        )}

        {archived.length > 0 && (
          <div className="mt-3">
            <button type="button" onClick={() => setShowArchived((v) => !v)} className="text-xs font-medium text-slate-500 hover:text-slate-800">
              {showArchived ? "Hide" : "Show"} {archived.length} archived
            </button>
            {showArchived && (
              <ul className="mt-1.5 divide-y divide-slate-100">
                {archived.map((b) => (
                  <li key={b.slug} className="flex items-center gap-3 py-1.5 text-sm text-slate-400">
                    <span className="flex-1">{b.name}</span>
                    <button type="button" onClick={() => save.mutate({ slug: b.slug, patch: { is_active: true } })} className="inline-flex items-center gap-1 text-xs font-medium text-brand-700 hover:text-brand-900"><RotateCcw className="h-3 w-3" /> Restore</button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}

        {unclaimed.length > 0 && (
          <div className="mt-4 rounded-xl border border-dashed border-slate-200 bg-slate-50/60 p-3">
            <div className="text-[11px] font-bold uppercase tracking-wide text-slate-500">Spellings no brand claims yet</div>
            <p className="mt-0.5 text-[11.5px] text-slate-500">What the equipment record says, with the unit count — add the real ones to a brand&apos;s spellings; the rest are types, not makes.</p>
            <div className="mt-2 flex flex-wrap gap-1.5">
              {unclaimed.map((s) => (
                <span key={s.spelling} className="rounded-full border border-slate-200 bg-white px-2 py-0.5 text-[11px] text-slate-600">{s.spelling} <span className="tabular-nums text-slate-400">{s.n.toLocaleString()}</span></span>
              ))}
            </div>
          </div>
        )}
      </div>
    </Card>
  );
}
